import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {Alert, Box, Button, Divider, Snackbar} from '@mui/material';

const MigrationTextView = ({migrationData}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return dateString.split('T')[0];
  };

  const textContent = useMemo(() => {
    if (!migrationData?.results?.migration_plan) {
      return 'No migration data available.';
    }

    const {context, overlay} = migrationData.results.migration_plan;
    const lines = [];

    // Header
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('MIGRATION PREVIEW');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('');
    lines.push(`Organizations: ${context.org_ids?.join(', ') || 'N/A'}`);
    lines.push('');

    // Build lookup maps
    const lineItemLinks = {};
    overlay.proposed_line_item_links?.forEach(link => {
      if (!lineItemLinks[link.line_item_id]) {
        lineItemLinks[link.line_item_id] = [];
      }
      lineItemLinks[link.line_item_id].push(link);
    });

    // Salesforce Products
    if (context.salesforce_products?.length > 0) {
      lines.push('');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('SALESFORCE PRODUCTS (Line Items)');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('');

      context.salesforce_products.forEach((product, index) => {
        const status = product.status || 'unknown';
        const statusLabel = status.toUpperCase();
        const emoji =
          status === 'active'
            ? '🟢'
            : status === 'expired'
              ? '⚪'
              : status === 'future'
                ? '🔵'
                : '⚠️';
        const links = lineItemLinks[product.line_item_id] || [];
        const bestLink = links.reduce(
          (best, link) =>
            !best || link.link_confidence > best.link_confidence ? link : best,
          null
        );
        const isOrphan = links.length === 0;

        lines.push(
          `${emoji} ${product.product_name || 'Unknown Product'} [${statusLabel}]`
        );
        lines.push(`   Line Item: ${product.line_item_id}`);
        if (product.pl_number) {
          lines.push(`   Reference: ${product.pl_number}`);
        }
        lines.push(
          `   Service Term: ${formatDate(product.start_date)} → ${formatDate(product.end_date)}`
        );

        // Show TOA if different from service dates
        const showTOA =
          (product.toa_start_date || product.toa_end_date) &&
          (product.toa_start_date !== product.start_date ||
            product.toa_end_date !== product.end_date);
        if (showTOA) {
          lines.push(
            `   Data Access: ${formatDate(product.toa_start_date) || 'Start of term'} → ${formatDate(product.toa_end_date) || 'End of term'}`
          );
        }

        if (product.quantity !== null && product.quantity !== undefined) {
          if (product.quantity === null) {
            lines.push(`   Quota: Unlimited`);
          } else {
            const units = product.unit_of_measure || 'units';
            lines.push(`   Quota: ${product.quantity} ${units}`);
          }
        }
        if (product.opportunity_name) {
          lines.push(`   Opportunity: ${product.opportunity_name}`);
        }
        if (product.edp_id) {
          lines.push(`   EDP ID: ${product.edp_id}`);
        }

        if (bestLink) {
          const confidence = Math.round(bestLink.link_confidence * 100);
          lines.push(
            `   → Linked to Subscription ID ${bestLink.subscription_id} (${confidence}% confidence)`
          );
        }

        if (isOrphan) {
          lines.push(`   ⚠️  Warning: No matching subscription found (orphan)`);
        }

        if (index < context.salesforce_products.length - 1) {
          lines.push('');
        }
      });
    }

    // Proposed Product Accesses
    if (context.product_accesses?.length > 0) {
      lines.push('');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('PROPOSED PRODUCT ACCESSES');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('');

      context.product_accesses.forEach((pa, index) => {
        const status = pa.status || 'unknown';
        const statusLabel = status.toUpperCase();
        const emoji =
          status === 'active'
            ? '🟢'
            : status === 'expired'
              ? '⚪'
              : status === 'future'
                ? '🔵'
                : '⚠️';

        lines.push(
          `${emoji} ${pa.edp_id || 'Product Access'} (Org ${pa.organization_id}) [${statusLabel}]`
        );
        if (pa.edp_id) {
          lines.push(`   EDP ID: ${pa.edp_id}`);
        }
        lines.push(
          `   Service Term: ${formatDate(pa.start_date)} → ${formatDate(pa.end_date)}`
        );

        // Show TOA if different from service dates
        const showTOA =
          (pa.toa_start_date || pa.toa_end_date) &&
          (pa.toa_start_date !== pa.start_date ||
            pa.toa_end_date !== pa.end_date);
        if (showTOA) {
          lines.push(
            `   Data Access: ${formatDate(pa.toa_start_date) || 'Start of term'} → ${formatDate(pa.toa_end_date) || 'End of term'}`
          );
        }

        if (pa.unlimited_quota) {
          lines.push(`   Quota: Unlimited`);
        } else if (pa.quota_limit !== null && pa.quota_limit !== undefined) {
          const units = pa.quota_units || 'units';
          lines.push(`   Quota: ${pa.quota_limit} ${units}`);
        }

        if (pa.quota_sqkm) {
          lines.push(`   Quota (normalized): ${pa.quota_sqkm} sq km`);
        }

        if (pa.line_item_id) {
          lines.push(`   → From Line Item: ${pa.line_item_id}`);
        }

        if (pa.existing_ids && pa.existing_ids.length > 0) {
          lines.push(
            `   → Similar to Existing PA: ${pa.existing_ids.join(', ')}`
          );
          if (pa.existing_similarity_score) {
            const similarity = Math.round(pa.existing_similarity_score * 100);
            lines.push(`      (${similarity}% match)`);
          }
        }

        if (index < context.product_accesses.length - 1) {
          lines.push('');
        }
      });
    }

    // Legacy Plans & Subscriptions
    if (context.legacy_plans?.length > 0) {
      lines.push('');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('LEGACY PLANS & SUBSCRIPTIONS');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('');

      context.legacy_plans.forEach((plan, index) => {
        const status = plan.status || 'unknown';
        const statusLabel = status.toUpperCase();
        const emoji =
          status === 'active'
            ? '🟠'
            : status === 'expired'
              ? '⚪'
              : status === 'future'
                ? '🔵'
                : '⚠️';

        // Check if subscription has line item links
        const subLinks =
          overlay.proposed_line_item_links?.filter(
            link => link.subscription_id === plan.subscription_id
          ) || [];
        const bestSubLink = subLinks.reduce(
          (best, link) =>
            !best || link.link_confidence > best.link_confidence ? link : best,
          null
        );
        const isOrphan = subLinks.length === 0;

        lines.push(
          `${emoji} ${plan.subscription_reference || plan.subscription_name || `Subscription ${plan.subscription_id}`} [${statusLabel}]`
        );
        lines.push(`   Subscription ID: ${plan.subscription_id}`);
        if (plan.plan_name) {
          lines.push(`   Plan: "${plan.plan_name}" (Plan ID: ${plan.plan_id})`);
        }
        lines.push(
          `   Active: ${formatDate(plan.active_from)} → ${formatDate(plan.active_to)}`
        );
        if (plan.quota_sqkm) {
          const quotaStyle = plan.quota_style || 'unknown';
          lines.push(`   Quota: ${plan.quota_sqkm} sq km (${quotaStyle})`);
        }
        lines.push(`   Organization: ${plan.organization_id}`);
        if (plan.billable_id) {
          lines.push(`   Billable: Yes (${plan.billable_id})`);
        }

        if (bestSubLink) {
          const confidence = Math.round(bestSubLink.link_confidence * 100);
          lines.push(
            `   → Linked to Line Item: ${bestSubLink.line_item_id} (${confidence}% confidence)`
          );
        }

        if (isOrphan) {
          lines.push(`   ⚠️  Warning: No matching line item found (orphan)`);
        }

        if (index < context.legacy_plans.length - 1) {
          lines.push('');
        }
      });
    }

    // Existing Product Accesses
    if (context.existing_product_accesses?.length > 0) {
      lines.push('');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('EXISTING PRODUCT ACCESSES');
      lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('');

      context.existing_product_accesses.forEach((epa, index) => {
        lines.push(
          `🔴 PA-${epa.id}: ${epa.edp_id || 'Product Access'} (Org ${epa.organization_id})`
        );

        // Show grouped terms if available
        if (epa.terms && epa.terms.length > 0) {
          lines.push(`   Terms (${epa.terms.length}):`);
          epa.terms.forEach((term, termIndex) => {
            lines.push('');
            lines.push(
              `   Term ${termIndex + 1}: ${formatDate(term.start_date)} → ${formatDate(term.end_date)}`
            );
            if (term.quota_limit) {
              const units = term.quota_units || 'units';
              lines.push(`            Quota: ${term.quota_limit} ${units}`);
            }
          });
          lines.push('');

          // Show aggregated quota
          const totalQuota = epa.terms.reduce(
            (sum, term) => sum + (term.quota_limit || 0),
            0
          );
          if (totalQuota > 0) {
            const units = epa.terms[0]?.quota_units || 'units';
            lines.push(`   Total Quota: ${totalQuota} ${units}`);
          }
        } else {
          // Fallback to aggregated display
          lines.push(
            `   Term: ${formatDate(epa.start_date)} → ${formatDate(epa.end_date)}`
          );

          // Show TOA if different from service dates
          const showTOA =
            (epa.toa_start_date || epa.toa_end_date) &&
            (epa.toa_start_date !== epa.start_date ||
              epa.toa_end_date !== epa.end_date);
          if (showTOA) {
            lines.push(
              `   Data Access: ${formatDate(epa.toa_start_date) || 'Start of term'} → ${formatDate(epa.toa_end_date) || 'End of term'}`
            );
          }

          if (epa.unlimited_quota) {
            lines.push(`   Quota: Unlimited`);
          } else if (epa.quota_limit) {
            const units = epa.quota_units || 'units';
            lines.push(`   Quota: ${epa.quota_limit} ${units}`);
          }
        }

        if (epa.quota_sqkm) {
          lines.push(`   Quota (normalized): ${epa.quota_sqkm} sq km`);
        }

        // Find proposed PAs that match this existing PA
        const matchingProposedPAs =
          context.product_accesses?.filter(pa =>
            pa.existing_ids?.includes(epa.id)
          ) || [];
        if (matchingProposedPAs.length > 0) {
          matchingProposedPAs.forEach(pa => {
            const similarity = pa.existing_similarity_score
              ? Math.round(pa.existing_similarity_score * 100)
              : null;
            lines.push(
              `   → Matched by Proposed PA for Line Item ${pa.line_item_id}${similarity ? ` (${similarity}% similarity)` : ''}`
            );
          });
        }

        if (index < context.existing_product_accesses.length - 1) {
          lines.push('');
        }
      });
    }

    // Summary
    lines.push('');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('SUMMARY');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('');

    const sfCount = context.salesforce_products?.length || 0;
    const sfActive =
      context.salesforce_products?.filter(p => p.status === 'active').length ||
      0;
    const sfExpired =
      context.salesforce_products?.filter(p => p.status === 'expired').length ||
      0;

    const paCount = context.product_accesses?.length || 0;
    const planCount = context.legacy_plans?.length || 0;
    const existingPACount = context.existing_product_accesses?.length || 0;

    const sfOrphans =
      context.salesforce_products?.filter(
        p => !(lineItemLinks[p.line_item_id]?.length > 0)
      ).length || 0;
    const planOrphans =
      context.legacy_plans?.filter(
        plan =>
          !overlay.proposed_line_item_links?.some(
            link => link.subscription_id === plan.subscription_id
          )
      ).length || 0;

    lines.push(
      `Salesforce Products: ${sfCount} (${sfActive} active, ${sfExpired} expired)`
    );
    lines.push(`Proposed Product Accesses: ${paCount}`);
    lines.push(`Legacy Subscriptions: ${planCount}`);
    lines.push(`Existing Product Accesses: ${existingPACount}`);
    if (sfOrphans > 0 || planOrphans > 0) {
      const orphanItems = [];
      if (sfOrphans > 0)
        orphanItems.push(`${sfOrphans} line item${sfOrphans > 1 ? 's' : ''}`);
      if (planOrphans > 0)
        orphanItems.push(
          `${planOrphans} subscription${planOrphans > 1 ? 's' : ''}`
        );
      lines.push(`Orphaned Items: ${orphanItems.join(', ')}`);
    }

    lines.push('');
    lines.push('═══════════════════════════════════════════════════════════');

    return lines.join('\n');
  }, [migrationData]);

  const handleCopy = () => {
    navigator.clipboard.writeText(textContent).then(() => {
      setCopySuccess(true);
    });
  };

  if (!migrationData?.results?.migration_plan) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="info">No migration plan data available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{p: 3}}>
      {/* Action Buttons */}
      <Box sx={{mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
        >
          Copy to Clipboard
        </Button>
      </Box>

      <Divider sx={{mb: 2}} />

      {/* Text Content */}
      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
          bgcolor: '#f5f5f5',
          p: 3,
          borderRadius: 1,
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {textContent}
      </Box>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Copied to clipboard"
      />
    </Box>
  );
};

MigrationTextView.propTypes = {
  migrationData: PropTypes.object.isRequired,
};

export default MigrationTextView;
